import { useState, type Dispatch, type SetStateAction } from 'react';
import { useOffline } from '../contexts/OfflineContext';
import { formatCurrency } from '../core/format/currency';
import { normalizeRouteStop, type RouteStop } from '../core/models/RouteStop';
import {
  canDeleteRouteStop,
  canSkipRouteStop,
  canBankerSecureCollect,
  applyBankerSecureCollect,
  canReviewDiscrepancy,
  applyDiscrepancyReview,
} from '../core/rules/routeStops';
import { canReviewSkipRequest } from '../core/rules/routeStopSkip';
import type { IDataAdapter } from '../adapters/data/IDataAdapter';
import type { AppNotification } from '../core/models/index';

type T = Record<string, string>;
type Push = (fields: Partial<AppNotification>, source?: string) => AppNotification | null;

type Opts = {
  t: T;
  push: Push;
  adapter: IDataAdapter;
  userId: string;
  houseId: string;
  departureOk: boolean;
  skipReason: string;
  setLocalStops: Dispatch<SetStateAction<Record<string, RouteStop>>>;
  setRemovedIds: Dispatch<SetStateAction<Record<string, true>>>;
  onAfterMutation?: () => void;
};

/** Secure-collect, skip review, and delete actions for banker route stops. */
export function useBankerRouteStopActions({
  t,
  push,
  adapter,
  userId,
  houseId,
  departureOk,
  skipReason,
  setLocalStops,
  setRemovedIds,
  onAfterMutation,
}: Opts) {
  const { online, enqueue } = useOffline();
  const [secureBusyId, setSecureBusyId] = useState<string | null>(null);
  const [reviewBusyId, setReviewBusyId] = useState<string | null>(null);
  const [skipBusy, setSkipBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const secureCollect = async (stop: RouteStop) => {
    if (secureBusyId || !canBankerSecureCollect(stop) || !departureOk) return;
    if (!adapter.secureCollectStop) return;
    const amount = Math.max(0, Number(stop.expectedAmount ?? 0));
    setSecureBusyId(stop.id);
    try {
      if (!online) {
        enqueue({
          type: 'secureCollectStop',
          payload: { stopId: stop.id, amount, actorId: userId, houseId },
        });
      } else {
        await adapter.secureCollectStop(
          { stopId: stop.id, amount, actorId: userId, houseId },
          { uid: userId, houseId },
        );
      }
      setLocalStops((prev) => ({
        ...prev,
        [stop.id]: applyBankerSecureCollect(stop, userId, Date.now(), amount),
      }));
      push({
        cat: 'routes',
        sev: 'success',
        title: t.secureCollectDone ?? t.confirmCollected,
        body: `${stop.name} · ${formatCurrency(amount)}`,
      });
    } catch {
      push({
        cat: 'routes',
        sev: 'danger',
        title: t.err_generic,
        body: t.secureCollectFailed ?? '',
      });
    } finally {
      setSecureBusyId(null);
    }
  };

  const markDiscrepancyReviewed = async (stop: RouteStop) => {
    if (reviewBusyId || !canReviewDiscrepancy(stop) || !adapter.updateRouteStop) return;
    const reviewed = applyDiscrepancyReview(stop, userId);
    setReviewBusyId(stop.id);
    try {
      await adapter.updateRouteStop({
        stopId: stop.id,
        patch: {
          discrepancyReviewedAt: reviewed.discrepancyReviewedAt ?? null,
          discrepancyReviewedBy: userId,
          discrepancyNote: reviewed.discrepancyNote ?? null,
        },
      });
      setLocalStops((prev) => ({ ...prev, [stop.id]: reviewed }));
      push({
        cat: 'routes',
        sev: 'success',
        title: t.discrepancyReviewed ?? t.discrepancy,
        body: `${stop.name} · ${formatCurrency(stop.collectedAmount ?? 0)} / ${formatCurrency(stop.listerDeliveredAmount ?? 0)}`,
      });
    } catch {
      push({
        cat: 'routes',
        sev: 'danger',
        title: t.err_generic,
        body: t.discrepancyReviewFailed ?? '',
      });
    } finally {
      setReviewBusyId(null);
    }
  };

  const skip = async (stop: RouteStop, reasonOverride?: string) => {
    if (skipBusy || !canSkipRouteStop(stop)) return;
    const reason = (reasonOverride ?? skipReason).trim() || 'skipped';
    const payload = { stopId: stop.id, reason, skippedBy: userId, houseId };
    setSkipBusy(true);
    try {
      if (!online || !adapter.skipRouteStop) {
        enqueue({ type: 'skipRouteStop', payload });
      } else {
        await adapter.skipRouteStop(payload);
      }
      setLocalStops((prev) => ({
        ...prev,
        [stop.id]: normalizeRouteStop({
          ...stop,
          status: 'skipped',
          skipReason: reason,
          skippedBy: userId,
          skipRequestStatus: null,
        }),
      }));
      push({
        cat: 'routes',
        sev: 'warn',
        title: t.skipStop,
        body: t.skipBankerAppliedBody
          ? String(t.skipBankerAppliedBody).replace('{name}', stop.name)
          : `${stop.name}: ${reason}`,
      });
      onAfterMutation?.();
    } catch {
      push({
        cat: 'routes',
        sev: 'danger',
        title: t.err_generic,
        body: t.skipStopFailed ?? t.skipStop,
      });
    } finally {
      setSkipBusy(false);
    }
  };

  const reviewSkip = async (stop: RouteStop, decision: 'approved' | 'rejected') => {
    if (skipBusy || !adapter.reviewRouteStopSkip || !canReviewSkipRequest(stop)) return;
    setSkipBusy(true);
    try {
      await adapter.reviewRouteStopSkip({
        stopId: stop.id,
        decision,
        houseId,
        reason: stop.skipRequestReason || undefined,
      });
      if (decision === 'approved') {
        setLocalStops((prev) => ({
          ...prev,
          [stop.id]: normalizeRouteStop({
            ...stop,
            status: 'skipped',
            skipReason: stop.skipRequestReason || 'skipped',
            skippedBy: userId,
            skipRequestStatus: null,
          }),
        }));
      } else {
        setLocalStops((prev) => ({
          ...prev,
          [stop.id]: normalizeRouteStop({
            ...stop,
            skipRequestStatus: null,
            skipRequestReason: null,
            skipRequestedBy: null,
          }),
        }));
      }
      push({
        cat: 'routes',
        sev: decision === 'approved' ? 'warn' : 'info',
        title: decision === 'approved'
          ? (t.skipApproved ?? t.skipStop)
          : (t.skipRejected ?? t.skipStop),
        body: stop.name,
      });
    } catch {
      push({
        cat: 'routes',
        sev: 'danger',
        title: t.err_generic,
        body: t.skipReviewFailed ?? t.skipStop,
      });
    } finally {
      setSkipBusy(false);
    }
  };

  const removeAdhoc = async (stop: RouteStop) => {
    if (deleteBusy || !canDeleteRouteStop(stop)) return;
    const payload = { stopId: stop.id };
    setDeleteBusy(true);
    setRemovedIds((prev) => ({ ...prev, [stop.id]: true }));
    try {
      if (!online || !adapter.deleteRouteStop) {
        enqueue({ type: 'deleteRouteStop', payload });
      } else {
        await adapter.deleteRouteStop(payload);
      }
      push({
        cat: 'routes',
        sev: 'warn',
        title: t.deleteAdhocStop ?? t.skipStop,
        body: stop.name,
      });
      onAfterMutation?.();
      setDeleteBusy(false);
    } catch {
      try {
        enqueue({ type: 'deleteRouteStop', payload });
        push({
          cat: 'routes',
          sev: 'warn',
          title: t.deleteAdhocStop ?? t.skipStop,
          body: stop.name,
        });
        onAfterMutation?.();
        setDeleteBusy(false);
      } catch {
        setRemovedIds((prev) => {
          const next = { ...prev };
          delete next[stop.id];
          return next;
        });
        push({
          cat: 'routes',
          sev: 'danger',
          title: t.deleteAdhocStopFailed ?? t.err_generic,
          body: stop.name,
        });
        setDeleteBusy(false);
      }
    }
  };

  return {
    secureBusyId,
    reviewBusyId,
    skipBusy,
    deleteBusy,
    setSkipBusy,
    setDeleteBusy,
    secureCollect,
    markDiscrepancyReviewed,
    skip,
    reviewSkip,
    removeAdhoc,
  };
}
